import type { ReportTypeId } from '../config/reportTypes';
import type { LiteratureEntry } from '../types/siteEvidence';
import { dedupLiteratureEntries } from './literatureDedup';
import { literaturePublicationExclusion } from './literaturePublication';

/** Versioned, deterministic topical screening; scores are not evidence confidence. */
export const LITERATURE_SELECTION_POLICY_VERSION = 'report-literature-v1';

type Topic = { name: string; pattern: RegExp };
const water: Topic = { name: 'water', pattern: /\b(water|wue|watershed|aquifer|groundwater|drought|desalination|hydrolog\w*)\b/ };
const cooling: Topic = { name: 'cooling', pattern: /\b(cooling|chiller\w*|liquid cooled|immersion|heat rejection|thermal management|heat exchanger\w*|heat reuse|waste heat)\b/ };
const waterAvailability: Topic = { name: 'water-availability', pattern: /\b(water rights|water stress|water scarcity|water availability|water security|water supply|aquifer\w*|groundwater|watershed\w*|drought|desalination|hydrolog\w*)\b/ };
const waterAccounting: Topic = { name: 'water-accounting', pattern: /\b(water consumption|water use|water demand|water withdrawal\w*|wue|water footprint|water energy nexus)\b/ };
const power: Topic = { name: 'power', pattern: /\b(electric\w*|power|grid|energy|nuclear|smr|reactor\w*|microgrid\w*|generation|turbine\w*)\b/ };
const siting: Topic = { name: 'siting', pattern: /\b(siting|site selection|site planning|land use|zoning|setback\w*|parcel\w*|geotechnical|flood\w*|seismic|layout|deployment)\b/ };
const environment: Topic = { name: 'environment', pattern: /\b(environment\w*|emission\w*|carbon|biodiversity|habitat\w*|ecolog\w*|pollution|nepa|sustainab\w*|footprint)\b/ };
const connection: Topic = { name: 'interconnection', pattern: /\b(interconnection|transmission|substation\w*|grid connection|ferc|hosting capacity|connection queue|grid congestion)\b/ };
const risk: Topic = { name: 'risk', pattern: /\b(risk\w*|reliability|resilien\w*|feasibility|trade off\w*|tradeoff\w*|decision|assessment|optimization|optimisation)\b/ };
const supply: Topic = { name: 'supply-chain', pattern: /\b(supply chain|procurement|vendor\w*|equipment|lead time\w*|manufactur\w*|logistics)\b/ };
const finance: Topic = { name: 'finance', pattern: /\b(cost\w*|economic\w*|financ\w*|investment|discount rate|wacc|lcoe|npv|irr|capital|tariff\w*)\b/ };
const publicVoice: Topic = { name: 'community', pattern: /\b(community|communities|stakeholder\w*|public acceptance|public opposition|public perception|engagement|consultation|social license|social licence|sentiment)\b/ };
const gas: Topic = { name: 'gas', pattern: /\b(gas|pipeline\w*|turbine\w*|microgrid\w*|behind the meter|air permit\w*)\b/ };

const REPORT_TOPICS: Record<ReportTypeId, readonly Topic[]> = {
  feasibility: [siting, risk, power, cooling, water],
  environmental: [environment, water, siting],
  'power-availability': [power, connection, risk],
  'water-resource': [water, cooling, waterAvailability, waterAccounting],
  interconnection: [connection],
  'supply-chain-risk': [supply, risk],
  'executive-summary': [siting, risk, power, cooling, water, environment, finance],
  'test-fit': [siting],
  'public-sentiment': [publicVoice],
  'custom-brief': [gas, power, finance, environment],
  financial: [finance],
};

const DATA_CENTRE = /\b(data cent(?:er|re)s?|datacent(?:er|re)s?|hyperscal\w*|server(?:s| farm\w*)?|computing facilit\w*|hpc)\b/;
const INFRASTRUCTURE = /\b(industrial|industries|industry|infrastructure|power plant\w*|power station\w*|power system\w*|power project\w*|electric\w*|grid|nuclear|smr|reactor\w*|microgrid\w*|energy facilit\w*|energy project\w*|energy system\w*|renewable energy|wind farm\w*|solar farm\w*|pipeline\w*|turbine\w*|transmission|substation\w*)\b/;
const WATER_METHOD = /\b(cooling tower\w*|cooling water|water rights|water stress|water scarcity|water availability|water withdrawal\w*|water supply|water demand|groundwater|aquifer\w*|watershed\w*|drought|desalination|hydrolog\w*|water energy nexus)\b/;
const SITE_METHOD = /\b(geotechnical|flood risk|flood hazard|seismic hazard|land use|biodiversity|habitat assessment|site planning)\b/;
const UNRELATED_TITLE = /\b(household\w*|residential|domestic hot water|drinking water consumption|patient\w*|clinical|dietary|hydration|renal|kidney|pediatric|paediatric)\b/;

function plain(value: string): string {
  return value.toLowerCase().replace(/<[^>]*>/g, ' ').replace(/&[a-z]+;/g, ' ').replace(/[‐‑–—-]/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Preprints remain eligible as labelled research context, never validated site facts. */
export function permitsResearchContext(entry: LiteratureEntry): boolean {
  // Policy extension point: publication form alone does not establish study quality.
  // Integrity notices are checked separately and cannot be overridden here.
  return Boolean(entry.title.trim());
}

export interface LiteratureSelectionAudit {
  policyVersion: string;
  reportType: ReportTypeId;
  candidateCount: number;
  admittedCount: number;
  excludedCount: number;
  duplicatesRemoved: number;
  selectedCount: number;
  excludedOmittedCount: number;
  excluded: Array<{ id: string; reasons: string[] }>;
  selected: Array<{ id: string; topics: string[]; scope: 'direct' | 'infrastructure' | 'methodology'; score: number }>;
}

/** Reassess legacy/cached entries, deduplicate, then rank before applying the cap. */
export function selectLiteratureForReport(
  entries: ReadonlyArray<LiteratureEntry>,
  reportType: ReportTypeId,
  maxEntries = 12,
): { entries: LiteratureEntry[]; audit: LiteratureSelectionAudit } {
  // Canonical ordering also makes duplicate tie-breaking independent of provider order.
  const ordered = entries.map(entry => ({ ...entry, relevanceScore: 0.5 }))
    .sort((a, b) => `${a.id}\n${a.title}`.localeCompare(`${b.id}\n${b.title}`));
  const deduped = dedupLiteratureEntries(ordered);
  const excluded: LiteratureSelectionAudit['excluded'] = [];
  const admitted: Array<{ entry: LiteratureEntry; topics: string[]; scope: 'direct' | 'infrastructure' | 'methodology'; score: number }> = [];
  for (const entry of deduped.entries) {
    const publicationReason = literaturePublicationExclusion(entry);
    if (publicationReason || !permitsResearchContext(entry)) {
      excluded.push({ id: entry.id, reasons: [publicationReason ?? 'missing-title'] });
      continue;
    }
    const title = plain(entry.title);
    const text = `${title} ${plain(entry.abstract?.trim() || entry.abstractSnippet || '')}`;
    const direct = DATA_CENTRE.test(text);
    const infrastructure = INFRASTRUCTURE.test(text);
    const methodology = reportType === 'water-resource'
      ? WATER_METHOD.test(text)
      : ['environmental', 'test-fit', 'feasibility', 'executive-summary'].includes(reportType) && SITE_METHOD.test(text);
    const matched = REPORT_TOPICS[reportType].filter(topic => topic.pattern.test(text));
    // A household/clinical paper mentioning data centres in passing is still off-topic.
    if ((UNRELATED_TITLE.test(title) && !DATA_CENTRE.test(title))
      || (/\bwater leak detection\b/.test(title) && !direct && !infrastructure)) {
      excluded.push({ id: entry.id, reasons: ['unrelated-primary-subject'] });
    } else if (matched.length === 0 || (!direct && !infrastructure && !methodology)) {
      excluded.push({ id: entry.id, reasons: ['no-report-topic-and-scope-match'] });
    } else {
      const titleMatches = matched.filter(topic => topic.pattern.test(title)).length;
      const scope = direct ? 'direct' : infrastructure ? 'infrastructure' : 'methodology';
      // A transparent match score, not a probability, peer-review rating or site confidence.
      const score = Math.min(0.95, (direct ? 0.72 : infrastructure ? 0.45 : 0.40)
        + Math.min(titleMatches, 3) * 0.04 + Math.min(matched.length, 3) * 0.015);
      admitted.push({ entry: { ...entry, relevanceScore: Number(score.toFixed(3)) }, topics: matched.map(topic => topic.name), scope, score });
    }
  }

  const limit = Number.isFinite(maxEntries) ? Math.max(0, Math.floor(maxEntries)) : 12;
  const remaining = [...admitted];
  const chosen: typeof admitted = [];
  const covered = new Set<string>();
  while (remaining.length && chosen.length < limit) {
    // Small topic-coverage bonus prevents equivalent early-query hits filling the cap.
    // It cannot make an ineligible paper admissible, or outweigh a strong scope match.
    const priority = (candidate: typeof admitted[number]) => candidate.score
      + candidate.topics.filter(topic => !covered.has(topic)).length * 0.025;
    remaining.sort((a, b) => priority(b) - priority(a) || b.score - a.score || a.entry.id.localeCompare(b.entry.id));
    const next = remaining.shift()!;
    chosen.push(next);
    next.topics.forEach(topic => covered.add(topic));
  }
  return {
    entries: chosen.map(candidate => candidate.entry),
    audit: {
      policyVersion: LITERATURE_SELECTION_POLICY_VERSION, reportType,
      candidateCount: entries.length, admittedCount: admitted.length,
      excludedCount: excluded.length, duplicatesRemoved: deduped.duplicatesRemoved,
      selectedCount: chosen.length, excluded: excluded.slice(0, 40),
      excludedOmittedCount: Math.max(0, excluded.length - 40),
      selected: chosen.map(({ entry, topics, scope }) => ({ id: entry.id, topics, scope, score: entry.relevanceScore })),
    },
  };
}

/** One projection for prompt content and new report references; never mutates a SED. */
export function getReportLiteratureEntries(
  entries: ReadonlyArray<LiteratureEntry>,
  reportType: ReportTypeId = 'feasibility',
): LiteratureEntry[] {
  return selectLiteratureForReport(entries, reportType).entries;
}
